import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Header,
  Box,
  Button,
  ButtonDropdown,
  Table,
  StatusIndicator,
  Icon,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { IMPORT_STATUS } from '../../../../import_data/constants/import_types';

const SurveyFilterSection = ({
  surveyFilterFile,
  setSurveyFilterFile,
  onDownloadHandler,
  getDownloadLabel,
  applicableToValue,
  onFileUpload,
  onFileDelete,
  filterUploadUUID,
  onUpdateStatus,
  importStatus,
  failedTemplateURL,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = event => {
    const file = event.target.files[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const handleAddFilter = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDelete = () => {
    setSurveyFilterFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileDelete();
  };

  const handleUpdateStatus = async () => {
    await onUpdateStatus(filterUploadUUID);
  };

  const getStatusIndicator = (status, errorReportURL) => {
    const hasFailures =
      status === IMPORT_STATUS.ValidationFailure ||
      status === IMPORT_STATUS.PartiallyImportedWithValidationFailure;

    const statusElement = (() => {
      switch (status) {
        case IMPORT_STATUS.Completed:
          return <StatusIndicator type="success">Completed</StatusIndicator>;
        case IMPORT_STATUS.Triggered:
        case IMPORT_STATUS.InProgress:
          return <StatusIndicator type="in-progress">In Progress</StatusIndicator>;
        case IMPORT_STATUS.ValidationFailure:
          return <StatusIndicator type="warning">Validation Failed</StatusIndicator>;
        case IMPORT_STATUS.PartiallyImportedWithValidationFailure:
          return (
            <StatusIndicator type="warning">Partially Imported - Validation Failed</StatusIndicator>
          );
        case IMPORT_STATUS.SystemFailure:
          return <StatusIndicator type="error">System Failed</StatusIndicator>;
        case IMPORT_STATUS.PartiallyImportedWithSystemFailure:
          return <StatusIndicator type="error">Partially Imported - System Failed</StatusIndicator>;
        case 'Failed':
          return <StatusIndicator type="error">Failed</StatusIndicator>;
        default:
          return <StatusIndicator type="pending">Unknown</StatusIndicator>;
      }
    })();

    return (
      <SpaceBetween direction="vertical" size="xs">
        {statusElement}
        {hasFailures && errorReportURL && (
          <a href={errorReportURL} target="_blank" rel="noopener noreferrer">
            Download Error Report
          </a>
        )}
      </SpaceBetween>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Email will only apply to surveys included in the file you upload."
          actions={
            <ButtonDropdown
              items={[
                { text: 'Filter Template', id: 'filter-template' },
                { text: getDownloadLabel(), id: 'all-surveys', disabled: !applicableToValue },
              ]}
              onItemClick={onDownloadHandler}
            >
              Download
            </ButtonDropdown>
          }
        >
          Survey Filter - optional
        </Header>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {!surveyFilterFile ? (
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <div>
            <Box variant="p" color="text-body-secondary" margin={{ bottom: 's' }}>
              No filter is added
            </Box>
          </div>
          <div>
            <Button onClick={handleAddFilter}>Add filter</Button>
          </div>
          <div>
            <Box variant="small" color="text-body-secondary" margin={{ top: 'xs' }}>
              Supported formats: .xlsx, .xls
            </Box>
          </div>
        </Box>
      ) : (
        <Table
          columnDefinitions={[
            {
              id: 'filename',
              header: 'Filename',
              cell: () => surveyFilterFile.name,
            },
            {
              id: 'status',
              header: (
                <Button variant="inline-link" onClick={() => handleUpdateStatus()}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <span>Status</span>
                    <Icon name="refresh" />
                  </SpaceBetween>
                </Button>
              ),
              cell: () => {
                return importStatus
                  ? getStatusIndicator(importStatus, failedTemplateURL)
                  : getStatusIndicator('Unknown');
              },
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: () => (
                <Button variant="inline-link" onClick={handleDelete}>
                  Delete
                </Button>
              ),
            },
          ]}
          items={[{ id: '1' }]}
          variant="container"
        />
      )}
    </Container>
  );
};

SurveyFilterSection.propTypes = {
  surveyFilterFile: PropTypes.object,
  setSurveyFilterFile: PropTypes.func.isRequired,
  onDownloadHandler: PropTypes.func.isRequired,
  getDownloadLabel: PropTypes.func.isRequired,
  applicableToValue: PropTypes.string,
  onFileUpload: PropTypes.func.isRequired,
  onFileDelete: PropTypes.func.isRequired,
  filterUploadUUID: PropTypes.string,
  onUpdateStatus: PropTypes.func.isRequired,
  importStatus: PropTypes.string,
  failedTemplateURL: PropTypes.string,
};

export default SurveyFilterSection;
